/* eslint-disable */
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { FiscalYearProvider } from './contexts/FiscalYearContext';
import MobileLayout from './components/layout/MobileLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import LocationTracking from './pages/LocationTracking';
import Users from './pages/Users';
import Activities from './pages/Activities';
import Teams from './pages/Teams';
import Geofences from './pages/Geofences';
import Customers from './pages/Customers';
import Products from './pages/ProductsEnhanced';
import Leads from './pages/Leads';
import CompanySettings from './pages/CompanySettings';
import Reports from './pages/Reports';
import SalesInvoices from './pages/SalesInvoices';
import SalesReports from './pages/SalesReports';
import Payments from './pages/Payments';
import DeliveryNotes from './pages/DeliveryNotes';
import ApprovalSettings from './pages/ApprovalSettings';
import ServiceRequests from './pages/ServiceRequests';
import MasterData from './pages/MasterData';
import DailyTasks from './pages/DailyTasks';
import BulkDelete from './pages/BulkDelete';
import AdminBackups from './pages/AdminBackups';
import MobileHome from './pages/MobileHome';
import AdminDiagnostics from './pages/AdminDiagnostics';
import VATReturn from './pages/VATReturn';
import OtherPurchases from './pages/OtherPurchases';
import FiscalYears from './pages/FiscalYears';
import FYAuditLog from './pages/FYAuditLog';
import AdminOverrideLog from './pages/AdminOverrideLog';
import StockReport from './pages/StockReport';
import CancellationRequests from './pages/CancellationRequests';
import Attendance from './pages/Attendance';
import AttendanceReport from './pages/AttendanceReport';
import SupplierPayments from './pages/SupplierPayments';
import LocationMap from './pages/LocationMap';
import Suppliers from './pages/Suppliers';
import PurchaseOrders from './pages/PurchaseOrders';
import PurchaseEntries from './pages/PurchaseEntries';
import { hasPermission } from './utils/permissions';
import '@/App.css';
import './mobile.css';
import './mobile-responsive.css';
import './mobile-aggressive.css';
import './mobile-portrait.css';
import './mobile-app-fixes.css';

// Protected Route Component with MobileLayout
// Bare protected route — auth-only, no MobileLayout chrome. Used by the
// mobile shell screens so they render full-bleed without the sidebar.
const BareProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-green-50 to-blue-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// `module` (optional) — when provided, checks the user's per-user permissions
// matrix. If the user lacks View on that module, they are redirected to their
// landing page. Admin / Super Admin always pass.
const ProtectedRoute = ({ children, module = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-green-50 to-blue-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Permissions check — if route declares a module, require View on it.
  // hasPermission() already short-circuits true for admin / super_admin and
  // falls back to role defaults when the user has no custom permissions map.
  if (module && !hasPermission(user, module, 'view')) {
    // Find a fallback page the user CAN see, in priority order
    const fallbacks = ['dashboard', 'daily_tasks', 'activities', 'customers', 'sales_invoices', 'payments'];
    const target = fallbacks.find((m) => hasPermission(user, m, 'view'));
    const routeMap = {
      dashboard: '/dashboard',
      daily_tasks: '/daily-tasks',
      activities: '/activities',
      customers: '/customers',
      sales_invoices: '/sales-invoices',
      payments: '/payments',
    };
    return <Navigate to={target ? routeMap[target] : '/login'} replace />;
  }

  return <MobileLayout>{children}</MobileLayout>;
};

// Decide the landing page based on what the user can actually see.
// Order = priority. Falls back to /login if user can't view anything.
const pickLanding = (user) => {
  const priority = [
    ['dashboard', '/dashboard'],
    ['activities', '/activities'],
    ['customers', '/customers'],
    ['sales_invoices', '/sales-invoices'],
    ['payments', '/payments'],
    ['leads', '/leads'],
    ['products', '/products'],
    ['daily_tasks', '/daily-tasks'],
  ];
  const match = priority.find(([m]) => hasPermission(user, m, 'view'));
  return match ? match[1] : '/login';
};

// Public Route Component (redirect to landing if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-green-50 to-blue-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={pickLanding(user)} replace />;
  }

  return children;
};

// Default route — sends user to their highest-priority allowed page
const RoleAwareDefault = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={pickLanding(user)} replace />;
};

function App() {
  // Register Service Worker for PWA — BUT NOT inside the Capacitor
  // native shell. The .apk loads the live URL and has its own offline
  // story (IndexedDB queue), so a SW just causes cache-stuck-on-old-
  // code bugs ("camera button not working after redeploy"). If a SW
  // is already registered from a previous load, unregister it and
  // wipe its caches so the next page load gets fresh JS.
  useEffect(() => {
    // Lazy import so this file doesn't pull capacitorBootstrap on web.
    import('@/native/capacitorBootstrap').then(({ isNativeApp }) => {
      if (isNativeApp()) {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then((regs) => {
            regs.forEach((r) => r.unregister());
          });
          if ('caches' in window) {
            caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
          }
        }
        return; // skip new SW registration in the native shell
      }
      if ('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
          navigator.serviceWorker
            .register('/service-worker.js')
            .then((registration) => {
              console.log('SW registered:', registration);
            })
            .catch((error) => {
              console.log('SW registration failed:', error);
            });
        });
      }
    });

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleOnline = () => {
    console.log('App is online');
    // Trigger background sync
    if ('serviceWorker' in navigator && 'sync' in navigator.serviceWorker) {
      navigator.serviceWorker.ready.then((registration) => {
        return registration.sync.register('sync-locations');
      });
    }
  };

  const handleOffline = () => {
    console.log('App is offline - data will sync when back online');
  };

  return (
    <AuthProvider>
      <CurrencyProvider>
        <FiscalYearProvider>
        <BrowserRouter>
          <LocationProvider>
            <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />

            {/* Protected Routes */}
            {/* Mobile App entry — Capacitor shell lands here. Render
                outside MobileLayout (no sidebar) by using the bare-bones
                ProtectedRoute variant inline. */}
            <Route
              path="/m/home"
              element={
                <BareProtectedRoute>
                  <MobileHome />
                </BareProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute module="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/location-tracking"
              element={
                <ProtectedRoute module="location_tracking">
                  <LocationTracking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute module="users">
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities"
              element={
                <ProtectedRoute module="activities">
                  <Activities />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teams"
              element={
                <ProtectedRoute module="teams">
                  <Teams />
                </ProtectedRoute>
              }
            />
            <Route
              path="/geofences"
              element={
                <ProtectedRoute module="location_tracking">
                  <Geofences />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute module="customers">
                  <Customers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute module="products">
                  <Products />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute module="leads">
                  <Leads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute module="company_settings">
                  <CompanySettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute module="reports">
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-invoices"
              element={
                <ProtectedRoute module="sales_invoices">
                  <SalesInvoices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/delivery-notes"
              element={
                <ProtectedRoute module="delivery_notes">
                  <DeliveryNotes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/service-requests"
              element={
                <ProtectedRoute module="service_requests">
                  <ServiceRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/approval-settings"
              element={
                <ProtectedRoute module="approval_settings">
                  <ApprovalSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-reports"
              element={
                <ProtectedRoute module="sales_reports">
                  <SalesReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/master-data"
              element={
                <ProtectedRoute module="master_data">
                  <MasterData />
                </ProtectedRoute>
              }
            />
            <Route
              path="/suppliers"
              element={
                <ProtectedRoute module="suppliers">
                  <Suppliers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase-orders"
              element={
                <ProtectedRoute module="purchase_orders">
                  <PurchaseOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase-entries"
              element={
                <ProtectedRoute module="purchase_entries">
                  <PurchaseEntries />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute module="payments">
                  <Payments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/daily-tasks"
              element={
                <ProtectedRoute module="daily_tasks">
                  <DailyTasks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendance"
              element={
                <ProtectedRoute module="attendance">
                  <Attendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendance-report"
              element={
                <ProtectedRoute module="attendance_report">
                  <AttendanceReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/supplier-payments"
              element={
                <ProtectedRoute module="supplier_payments">
                  <SupplierPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bulk-delete"
              element={
                <ProtectedRoute module="bulk_delete">
                  <BulkDelete />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/backups"
              element={
                <ProtectedRoute>
                  <AdminBackups />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/diagnostics"
              element={
                <ProtectedRoute>
                  <AdminDiagnostics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vat-return"
              element={
                <ProtectedRoute>
                  <VATReturn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/other-purchases"
              element={
                <ProtectedRoute>
                  <OtherPurchases />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fiscal-years"
              element={
                <ProtectedRoute>
                  <FiscalYears />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fy-audit-log"
              element={
                <ProtectedRoute>
                  <FYAuditLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/override-log"
              element={
                <ProtectedRoute>
                  <AdminOverrideLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stock-report"
              element={
                <ProtectedRoute>
                  <StockReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cancellation-requests"
              element={
                <ProtectedRoute>
                  <CancellationRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/location-map"
              element={
                <ProtectedRoute>
                  <LocationMap />
                </ProtectedRoute>
              }
            />


            {/* Default Route - employees go to Daily Tasks, others to Dashboard */}
            <Route path="/" element={<RoleAwareDefault />} />
            <Route path="*" element={<RoleAwareDefault />} />
          </Routes>
        </LocationProvider>
      </BrowserRouter>
        </FiscalYearProvider>
    </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
